import {
  Box,
  Button,
  Switch,
  Text,
  Flex,
  Image,
  VStack,
  HStack,
  useToast,
  Divider
} from '@chakra-ui/react';
import { FC, useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { seasonClawPrizeSchema, SeasonClawPrizeFormData } from '@olagg/validation-schemas';
import { AdminSeasonClawMachinePrize } from '@olagg/db-types';
import OlaGGFormInput from '../../../components/Forms/OlaggFormInput';
import OlaggForm from '../../../components/Forms/OlaggForm';
import OlaggFormSelect from '../../../components/Forms/OlaggFormSelect';
import usePrizes from '../../ClawMachine/hooks/usePrizes';

type IProps = {
  initialData?: AdminSeasonClawMachinePrize | undefined;
  onSubmit: (data: SeasonClawPrizeFormData) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  isEdit?: boolean;
};

const SeasonClawPrizeForm: FC<IProps> = ({ initialData, onSubmit, onCancel, loading = false, isEdit = false }) => {
  const toast = useToast();
  const { getPrizes } = usePrizes();

  const stockOptions = [
    {
      value: 'limited',
      label: 'Limitado'
    },
    {
      value: 'unlimited',
      label: 'Ilimitado'
    }
  ]

  const [prizesOptions, setPrizesOptions] = useState<any[]>([]);
  const [selectedPrize, setSelectedPrize] = useState<any>(null);
  const [cheatCodePositions, setCheatCodePositions] = useState<{ x: number; z: number }[]>([]);

  const loadPrizesOptions = useCallback(async (inputValue: string) => {
    try {
      const params = {
        page: 1,
        limit: 100,
        title: inputValue || undefined
      }
      const response = await getPrizes(params);
      const data = await response.clawMachinePrizes;
      const options = data.map((item: any) => ({
        value: item.id,
        label: item.title,
        rewardType: item.rewardType, // Incluir rewardType
        data: item // Incluir todos los datos del premio
      }));
      return options;
    } catch (error) {
      console.error("Error loading prizes options:", error);
      return [];
    }
  }, [getPrizes]);

  useEffect(() => {
    loadPrizesOptions('').then(options => setPrizesOptions(options));
  }, []);

  const useFormMethods = useForm<SeasonClawPrizeFormData>({
    resolver: yupResolver(seasonClawPrizeSchema),
    mode: 'onChange',
    defaultValues: {
      active: initialData?.active || false,
      clawMachinePrizeId: initialData?.clawMachinePrizeId || '',
      stockType: initialData?.stockType || 'limited',
      rewardValue: initialData?.rewardValue || 0,
      quantity: initialData?.quantity || 0,
      probability: initialData?.probability || 0.0,
      cheatCodePositions: initialData?.cheatCodePositions || [],
      cheatCodeProbability: initialData?.cheatCodeProbability || 0.0
    }
  });

  // Agregar la opción actual del initialData si existe y no está en las opciones
    useEffect(() => {
      if (isEdit && initialData?.clawMachinePrizeId && initialData?.title) {
        const currentOption = {
          value: String(initialData.clawMachinePrizeId),
          label: initialData.title || 'Untitled'
        };
  
        setPrizesOptions((prevOptions) => {
          const exists = prevOptions.some(option => option.value === currentOption.value);
          if (!exists) {
            return [currentOption, ...prevOptions];
          }
          return prevOptions;
        });
      }
    }, [isEdit, initialData?.clawMachinePrizeId, initialData?.title]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch
  } = useFormMethods;

  const watchedStockType = watch('stockType');
  const watchedClawMachinePrizeId = watch('clawMachinePrizeId');
  const watchedCheatCodePositions = watch('cheatCodePositions');

  // Tipos de recompensa que deben ocultar el campo rewardValue
  const hiddenRewardValueTypes = ['treasure_chance', 'claw_coin', 'token', 'store_product', 'physical_item'];

  // Encontrar el premio seleccionado y determinar si debe ocultar rewardValue
  const shouldHideRewardValue = selectedPrize && hiddenRewardValueTypes.includes(selectedPrize.rewardType);

  // Determinar si debe mostrar el campo de probabilidad de cheat code
  const shouldShowCheatCodeProbability = watchedCheatCodePositions && watchedCheatCodePositions.length > 0;
  
  // Determinar si ya hay una posición configurada
  const hasCheatCodePosition = watchedCheatCodePositions && watchedCheatCodePositions.length > 0;

  // Funciones para manejar cheat code position (solo una)
  const addCheatCodePosition = () => {
    setValue('cheatCodePositions', [{ x: 0, z: 0 }]);
  };

  const removeCheatCodePosition = () => {
    setValue('cheatCodePositions', []);
    setValue('cheatCodeProbability', 0.0);
  };

  const updateCheatCodePosition = (field: 'x' | 'z', value: number) => {
    const currentPositions = watchedCheatCodePositions || [];
    if (currentPositions.length > 0) {
      const newPosition = { ...currentPositions[0], [field]: value };
      setValue('cheatCodePositions', [newPosition]);
    }
  };

  // Limpiar quantity cuando stockType cambia a 'unlimited'
  useEffect(() => {
    if (watchedStockType === 'unlimited') {
      setValue('quantity', undefined);
    }
  }, [watchedStockType, setValue]);

  // Cargar las posiciones de cheat code existentes al editar
  useEffect(() => {
    if (isEdit && initialData?.cheatCodePositions) {
      setValue('cheatCodePositions', initialData.cheatCodePositions as { x: number; z: number }[]);
    }
  }, [isEdit, initialData?.cheatCodePositions, setValue]);

  // Manejar cambios en la selección del prize
  useEffect(() => {
    if (watchedClawMachinePrizeId) {
      // Buscar el premio seleccionado en las opciones
      const prize = prizesOptions.find(option => option.value === watchedClawMachinePrizeId);
      setSelectedPrize(prize?.data || null);
      
      // Si el premio tiene un tipo que debe ocultar rewardValue, establecer valor 1
      if (prize?.rewardType && hiddenRewardValueTypes.includes(prize.rewardType)) {
        setValue('rewardValue', 1);
      }
    } else {
      setSelectedPrize(null);
    }
  }, [watchedClawMachinePrizeId, prizesOptions, setValue]);


  const beforeSubmit = async (data: SeasonClawPrizeFormData) => {
    const finalData: SeasonClawPrizeFormData = {
      ...data,
      // Si el campo está oculto, asegurar que tenga valor 1
      rewardValue: shouldHideRewardValue ? 1 : data.rewardValue,
      // Si no hay cheatCodePositions, enviar cheatCodeProbability como 0.0
      cheatCodeProbability: (!data.cheatCodePositions || data.cheatCodePositions.length === 0) ? 0.0 : data.cheatCodeProbability,
    };
    await onSubmit(finalData);
  };

  return (
    <OlaggForm
      onSubmit={beforeSubmit}
      useFormMethods={useFormMethods}
      style={{
        width: '100%'
      }}
    >
      <VStack color={'white'} w={'full'} maxW='600px' margin='0 auto'>
        {/* Premio del catalogo */}
        <OlaggFormSelect
          label='Premio a configurar para esta temporada'
          placeholder='Selecciona un premio'
          description='Selecciona el premio del catálogo que se agregará a esta temporada'
          staticOptions={prizesOptions}
          asyncOptions={loadPrizesOptions}
          controlName='clawMachinePrizeId'
          newVersion
          isSearchable
          required={true}
        />

        {/* Tipo de Stock */}
        <OlaggFormSelect
          label='Tipo de Stock'
          description='Define si el premio tiene cantidad limitada o ilimitada'
          placeholder='Selecciona un tipo de stock'
          staticOptions={stockOptions}
          controlName='stockType'
          newVersion
          required={true}
        />

        {/* Valor del Premio - Solo visible si el rewardType no está en la lista de ocultos */}
        {!shouldHideRewardValue && (
          <OlaGGFormInput
            label='Valor del Premio'
            description='Valor de la recompensa que recibe el usuario al ganar este premio. Ej: 100 (100 xp)'
            controlName='rewardValue'
            inputType='number'
            required={true}
          />
        )}

        {/* Cantidad - Solo visible cuando stockType es 'limited' */}
        {watchedStockType === 'limited' && (
          <OlaGGFormInput
            label='Cantidad'
            description='Cantidad de premios disponibles para esta temporada'
            controlName='quantity'
            inputType='number'
            required={true}
          />
        )}

        {/* Probabilidad */}
        <OlaGGFormInput
          label='Probabilidad'
          description='Probabilidad base de que este premio salga ganador (0.0 = nunca, 1.0 = siempre)'
          controlName='probability'
          inputType='number'
          required={true}
        />

        {/* Cheat Code Positions */}
        <Box w="full">
          <Text color="white" fontSize="sm" mb={2}>
            Posición de Cheat Code
          </Text>
          <Text color="gray.400" fontSize="xs" mb={3}>
            Define una posición especial [x, z] donde el premio tiene mayor probabilidad
          </Text>
          
          {hasCheatCodePosition ? (
            <Flex gap={2} mb={2} alignItems="center">
              <Box flex="1">
                <Text color="white" fontSize="xs" mb={1}>X:</Text>
                <input
                  type="number"
                  value={watchedCheatCodePositions[0]?.x || 0}
                  onChange={(e) => updateCheatCodePosition('x', Number(e.target.value))}
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #4a5568',
                    backgroundColor: '#2d3748',
                    color: 'white'
                  }}
                />
              </Box>
              <Box flex="1">
                <Text color="white" fontSize="xs" mb={1}>Z:</Text>
                <input
                  type="number"
                  value={watchedCheatCodePositions[0]?.z || 0}
                  onChange={(e) => updateCheatCodePosition('z', Number(e.target.value))}
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #4a5568',
                    backgroundColor: '#2d3748',
                    color: 'white'
                  }}
                />
              </Box>
              <Button
                size="sm"
                colorScheme="red"
                onClick={removeCheatCodePosition}
                mt={4}
              >
                ✕
              </Button>
            </Flex>
          ) : (
            <Button
              size="sm"
              colorScheme="blue"
              onClick={addCheatCodePosition}
              w="full"
            >
              + Agregar Posición de Cheat Code
            </Button>
          )}
        </Box>

        {/* Cheat Code Probability - Solo visible si hay posiciones */}
        {shouldShowCheatCodeProbability && (
          <OlaGGFormInput
            label='Probabilidad de Cheat Code'
            description='Probabilidad especial cuando se usan códigos trampa en posiciones específicas (0.0 = nunca, 1.0 = siempre)'
            controlName='cheatCodeProbability'
            inputType='number'
            required={false}
          />
        )}

        {/* Visibilidad */}
        <OlaGGFormInput
          label='Activo'
          controlName='active'
          inputType='switch'
          required={false}
        />

        <Button
          variant={'filledGradient'}
          type="submit"
          w={'full'}
          style={{
            marginTop: '30px'
          }}
          isLoading={loading}
          loadingText={isEdit ? 'Actualizando...' : 'Creando...'}
        >
          {isEdit ? 'Actualizar Premio' : 'Crear Premio'}
        </Button>
      </VStack>
    </OlaggForm>
  );
};

export default SeasonClawPrizeForm;
export { SeasonClawPrizeForm as Form };
